import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import type { CognitiveBias } from '../../types';
import { Portal } from '../ui/Portal';
import { Input } from '../ui';

interface SearchInputProps {
  biases: CognitiveBias[];
  onBiasSelect: (bias: CognitiveBias) => void;
}

export const SearchInput: React.FC<SearchInputProps> = ({ biases, onBiasSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const isMobile = useRef(window.innerWidth < 768);

  const suggestions = searchTerm
    ? biases
        .filter(bias => 
          bias.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const getRecommendedBiases = () => {
    return biases
      .filter(bias => 
        bias.name.toLowerCase().includes('anchoring') || 
        bias.name.toLowerCase().includes('confirmation') ||
        bias.name.toLowerCase().includes('sunk cost')
      )
      .slice(0, 5);
  };

  const updatePosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      
      // Update mobile check
      isMobile.current = window.innerWidth < 768;
      
      if (isMobile.current) {
        // On mobile, make the suggestions dropdown fixed to the top of the screen
        // and full width, directly below the search bar
        setPosition({
          top: rect.bottom,
          left: 0,
          width: window.innerWidth,
        });
      } else {
        // On desktop, position it relative to the viewport
        // Since the navbar is fixed/sticky, we use the viewport-relative position
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    }
  };

  const handleBiasSelect = (bias: CognitiveBias) => {
    // Call the provided onBiasSelect handler
    onBiasSelect(bias);
    
    // Clear the search term and hide suggestions
    setSearchTerm('');
    setShowSuggestions(false);
  };

  // Update position when showing suggestions
  useEffect(() => {
    if (showSuggestions) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSuggestions]);

  // Update position on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (showSuggestions) {
        updatePosition();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showSuggestions]);

  return (
    <div ref={inputRef} className="w-full">
      <div className="relative flex items-center w-full">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
          <Search 
            size={16} 
            className="text-gray-400"
            strokeWidth={1.5}
          />
        </span>
        <Input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Search biases..."
          className="pl-9"
        />
      </div>

      {showSuggestions && (suggestions.length > 0 || !searchTerm) && (
        <Portal>
          <div 
            ref={suggestionsRef}
            className="fixed bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl shadow-xl overflow-auto z-[9999]"
            style={{
              position: 'fixed', // Always use fixed positioning
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
              maxHeight: '300px',
            }}
          >
            {searchTerm ? (
              suggestions.map(bias => (
                <div
                  key={bias.id}
                  onClick={() => handleBiasSelect(bias)}
                  className="px-4 py-3 hover:bg-orange-50 active:bg-orange-100 cursor-pointer transition-colors duration-200"
                >
                  <span>{bias.name}</span>
                  <span className="text-orange-400 text-xs ml-2">· {bias.category}</span>
                </div>
              ))
            ) : (
              <>
                <div className="px-4 py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Popular biases</span>
                </div>
                {getRecommendedBiases().map(bias => (
                  <div
                    key={bias.id}
                    onClick={() => handleBiasSelect(bias)}
                    className="px-4 py-3 hover:bg-orange-50 active:bg-orange-100 cursor-pointer transition-colors duration-200"
                  >
                    <span>{bias.name}</span>
                    <span className="text-orange-400 text-xs ml-2">· {bias.category}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </Portal>
      )}

      {showSuggestions && (
        <Portal>
          <div 
            className="fixed inset-0"
            style={{ backgroundColor: 'transparent', zIndex: 9998 }}
            onClick={() => setShowSuggestions(false)}
          />
        </Portal>
      )}
    </div>
  );
}; 